import { SearchBar } from '@/components/home/search-bar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Music, Heart, Waves, Volume2, Wind } from 'lucide-react';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { useContentList } from '@/lib/hooks/use-content';
import Loading from '@/components/loading';
import AudioCard from '@/components/audio-card';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';
import { canSeeBinauralBeats, getDeepLink } from '@/lib/constants';
import { useNavigate } from 'react-router-dom';

export default function FavorietenPage() {
  const navigate = useNavigate();
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const { favorites, toggleFavorite } = useFavorites();
  const { contentWithConfigs: minutjes, loading: minutjesLoading } =
    useContentList('minutjes');
  const { contentWithConfigs: meditations, loading: meditationsLoading } =
    useContentList('meditation');
  const { contentWithConfigs: journeys, loading: journeysLoading } =
    useContentList('journey');

  const { contentWithConfigs: music, loading: musicLoading } =
    useContentList('music');
  const { contentWithConfigs: natuurgeluiden, loading: natuurgeluidenLoading } =
    useContentList('nature_sound');
  const { contentWithConfigs: binauralBeats, loading: binauralBeatsLoading } =
    useContentList('binaural');
  const { contentWithConfigs: breathingExercises, loading: breathingLoading } =
    useContentList('breathing');
  const { contentWithConfigs: bodyExercises, loading: bodyLoading } =
    useContentList('body_exercise');
  const { contentWithConfigs: danceBreaks, loading: danceLoading } =
    useContentList('dance_break');

  const isLoading =
    loadingProfile ||
    minutjesLoading ||
    meditationsLoading ||
    musicLoading ||
    natuurgeluidenLoading ||
    binauralBeatsLoading ||
    breathingLoading ||
    bodyLoading ||
    danceLoading;

  if (isLoading) return <Loading />;

  return (
    <div className="min-h-screen p-8 space-y-8">
      <div className="mb-8 space-y-6">
        {/* <div className="max-w-xl">
          <SearchBar />
        </div> */}
        <h1 className="text-2xl font-semibold">Favorieten</h1>
      </div>

      <Tabs defaultValue="meditaties" className="w-full">
        <TabsList className="flex flex-wrap sm:flex-nowrap w-full justify-start border-b rounded-none p-0 h-auto">
          <TabsTrigger
            value="meditaties"
            className="flex items-center gap-2 data-[state=active]:border-b-2 border-green-500 rounded-none px-4 py-2 w-1/2 sm:w-auto justify-center sm:justify-start"
          >
            <Heart className="h-4 w-4" />
            Meditaties
          </TabsTrigger>
          <TabsTrigger
            value="muziek"
            className="flex items-center gap-2 data-[state=active]:border-b-2 border-green-500 rounded-none px-4 py-2 w-1/2 sm:w-auto justify-center sm:justify-start"
          >
            <Music className="h-4 w-4" />
            Muziek / Frequenties
          </TabsTrigger>
          <TabsTrigger
            value="natuurgeluiden"
            className="flex items-center gap-2 data-[state=active]:border-b-2 border-green-500 rounded-none px-4 py-2 w-1/2 sm:w-auto justify-center sm:justify-start"
          >
            <Waves className="h-4 w-4" />
            Natuurgeluiden
          </TabsTrigger>
          {canSeeBinauralBeats(isAdmin) && (
            <TabsTrigger
              value="binaural"
              className="flex items-center gap-2 data-[state=active]:border-b-2 border-green-500 rounded-none px-4 py-2 w-1/2 sm:w-auto justify-center sm:justify-start"
            >
              <Volume2 className="h-4 w-4" />
              Binaural Beats
            </TabsTrigger>
          )}
          <TabsTrigger
            value="oefeningen"
            className="flex items-center gap-2 data-[state=active]:border-b-2 border-green-500 rounded-none px-4 py-2 w-1/2 sm:w-auto justify-center sm:justify-start"
          >
            <Wind className="h-4 w-4" />
            Oefeningen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="meditaties" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[...minutjes, ...meditations, ...journeys]
              .filter((item) => favorites[item.content.id])
              .map((item) => (
                <AudioCard
                  key={item.content.id}
                  content={item.content}
                  isFavorite={favorites[item.content.id]}
                  onClick={() => {
                    navigate(
                      getDeepLink(item.content.content_type, item.content.id),
                    );
                  }}
                  onFavoriteToggle={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.content.id);
                  }}
                />
              ))}
          </div>
        </TabsContent>

        <TabsContent value="muziek" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {music
              .filter((item) => favorites[item.content.id])
              .map((item) => (
                <AudioCard
                  key={item.content.id}
                  content={item.content}
                  isFavorite={favorites[item.content.id]}
                  onClick={() =>
                    navigate(
                      getDeepLink(item.content.content_type, item.content.id),
                    )
                  }
                  onFavoriteToggle={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.content.id);
                  }}
                />
              ))}
          </div>
        </TabsContent>

        <TabsContent value="natuurgeluiden" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {natuurgeluiden
              .filter((item) => favorites[item.content.id])
              .map((item) => (
                <AudioCard
                  key={item.content.id}
                  content={item.content}
                  isFavorite={favorites[item.content.id]}
                  onClick={() =>
                    navigate(
                      getDeepLink(item.content.content_type, item.content.id),
                    )
                  }
                  onFavoriteToggle={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.content.id);
                  }}
                />
              ))}
          </div>
        </TabsContent>

        <TabsContent value="binaural" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {binauralBeats
              .filter((item) => favorites[item.content.id])
              .map((item) => (
                <AudioCard
                  key={item.content.id}
                  content={item.content}
                  isFavorite={favorites[item.content.id]}
                  onFavoriteToggle={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.content.id);
                  }}
                  onClick={() =>
                    navigate(
                      getDeepLink(item.content.content_type, item.content.id),
                    )
                  }
                />
              ))}
          </div>
        </TabsContent>

        <TabsContent value="oefeningen" className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[...breathingExercises, ...bodyExercises, ...danceBreaks]
              .filter((item) => favorites[item.content.id])
              .map((item) => (
                <AudioCard
                  key={item.content.id}
                  content={item.content}
                  isFavorite={favorites[item.content.id]}
                  onClick={() =>
                    navigate(
                      getDeepLink(item.content.content_type, item.content.id),
                    )
                  }
                  onFavoriteToggle={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.content.id);
                  }}
                />
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
